export const ITSM_SIDEBAR_TEST_ID = 'itsm-sidebar';

export type ItsmMeFlags = {
  isAgent?: boolean;
  portalAdmin?: boolean;
};

export type ItsmNavFlags = {
  agentOnly?: boolean;
  portalAdminOnly?: boolean;
};

export type ItsmNavLink = ItsmNavFlags & {
  kind: 'link';
  routeKey: string;
  href: string;
  label: string;
};

export type ItsmNavFolder = ItsmNavFlags & {
  kind: 'folder';
  label: string;
  children: ItsmNavLink[];
};

export type ItsmNavNode = ItsmNavLink | ItsmNavFolder;

export function routeAllowedForUser(me: ItsmMeFlags | null | undefined, flags: ItsmNavFlags): boolean {
  if (flags.portalAdminOnly && !me?.portalAdmin) return false;
  if (flags.agentOnly && !me?.isAgent) return false;
  return true;
}

function link(routeKey: string, href: string, label: string, flags: ItsmNavFlags = {}): ItsmNavLink {
  return { kind: 'link', routeKey, href, label, ...flags };
}

export const DEFAULT_ITSM_NAV_TREE: ItsmNavNode[] = [
  link('home', '/servicedesk/#home', 'Service desk'),
  link('my-tickets', '/servicedesk/#my-tickets', 'My tickets'),
  link('new-ticket', '/servicedesk/#new-ticket', 'New ticket'),
  link('knowledge-base', '/servicedesk/#knowledge-base', 'Knowledge base'),
  {
    kind: 'folder',
    label: 'Agent',
    agentOnly: true,
    children: [
      link('backlog', '/servicedesk/#backlog', 'Backlog', { agentOnly: true }),
      link('incidents', '/servicedesk/#incidents', 'Incidents', { agentOnly: true }),
      link('work-requests', '/servicedesk/#work-requests', 'Work requests', { agentOnly: true }),
      link('problems', '/servicedesk/#problems', 'Problems', { agentOnly: true }),
      link('changes', '/servicedesk/#changes', 'Changes', { agentOnly: true }),
      link('reference', '/servicedesk/#reference', 'Reference', { agentOnly: true }),
    ],
  },
  link('admin', '/servicedesk/#admin', 'Admin', { portalAdminOnly: true }),
  link('gatekeeper', '/gatekeeper/', 'Gatekeeper', { agentOnly: true }),
];

export function filterNavTree(tree: ItsmNavNode[], me: ItsmMeFlags | null | undefined): ItsmNavNode[] {
  const out: ItsmNavNode[] = [];
  for (const node of tree) {
    if (!routeAllowedForUser(me, node)) continue;
    if (node.kind === 'folder') {
      const children = node.children.filter((child) => routeAllowedForUser(me, child));
      if (!children.length) continue;
      out.push({ ...node, children });
      continue;
    }
    out.push(node);
  }
  return out;
}

/** Hash key from an ITSM href (`/servicedesk/#work-requests` → `work-requests`). */
export function hashKeyFromItsmHref(href: string): string | null {
  if (href.startsWith('/gatekeeper')) return null;
  const i = href.indexOf('#');
  if (i < 0) return null;
  return href.slice(i + 1).replace(/^\//, '').split('?')[0] || 'home';
}

export function itsmHashNavOnDesk(href: string, pathname: string): boolean {
  return (
    hashKeyFromItsmHref(href) != null && pathname.replace(/\/$/, '').endsWith('/servicedesk')
  );
}

export function titleForItsmLocation(pathname: string, hash: string): string {
  const path = pathname.replace(/\/$/, '') || '/';
  if (path.endsWith('/gatekeeper')) return 'Gatekeeper';
  const key = hash.replace(/^#\/?/, '').split('?')[0] || 'home';
  const labels: Record<string, string> = {
    home: 'Service desk',
    'my-tickets': 'My tickets',
    'new-ticket': 'New ticket',
    'knowledge-base': 'Knowledge base',
    incidents: 'Incidents',
    'work-requests': 'Work requests',
    changes: 'Changes',
    problems: 'Problems',
    backlog: 'Backlog',
    reference: 'Reference',
    admin: 'Admin',
  };
  return labels[key] || 'Communio ITSM';
}
